import { AUTH_URL } from '@bn-games/auth-client';

/**
 * The one HTTP path for every social endpoint: URL building, cookie
 * credentials, status → error mapping and body decoding live here so the eight
 * public functions in `friends.ts` / `invites.ts` are each a single call.
 *
 * Unlike `auth-client`'s `fetchMe`, failures are NOT collapsed into `null`.
 * This API's failure modes are meaningful to the user — a privacy setting
 * blocked the invite (403), someone already answered it (409), it expired
 * (410), you are inviting too fast (429) — so they are returned as data rather
 * than thrown: a signed-out visitor is an expected state, not an exception.
 */

export type SocialErrorKind =
  | 'unauthenticated'
  | 'bad_request'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'expired'
  | 'rate_limited'
  | 'network'
  | 'unknown';

export interface SocialError {
  kind: SocialErrorKind;
  /** HTTP status, or `0` when the request never reached the server. */
  status: number;
  /** The server's `{ error }` message when it sent one, else a fallback. */
  message: string;
  /** Present on `rate_limited` when the response carried `Retry-After`. */
  retryAfterSeconds?: number;
}

export type SocialResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: SocialError };

/** Narrows an unknown decoded body without committing to `any`. */
export type Decoder<T> = (body: unknown) => T | null;

const NETWORK_STATUS = 0;
const STATUS_UNAUTHORIZED = 401;
const STATUS_FORBIDDEN = 403;
const STATUS_NOT_FOUND = 404;
const STATUS_CONFLICT = 409;
const STATUS_GONE = 410;
const STATUS_TOO_MANY_REQUESTS = 429;
const STATUS_BAD_REQUEST = 400;
const STATUS_OK_MIN = 200;
const STATUS_OK_MAX = 299;
const DECIMAL_RADIX = 10;

const KIND_BY_STATUS: ReadonlyMap<number, SocialErrorKind> = new Map<number, SocialErrorKind>([
  [STATUS_BAD_REQUEST, 'bad_request'],
  [STATUS_UNAUTHORIZED, 'unauthenticated'],
  [STATUS_FORBIDDEN, 'forbidden'],
  [STATUS_NOT_FOUND, 'not_found'],
  [STATUS_CONFLICT, 'conflict'],
  [STATUS_GONE, 'expired'],
  [STATUS_TOO_MANY_REQUESTS, 'rate_limited'],
]);

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function ok<T>(value: T): SocialResult<T> {
  return { ok: true, value };
}

export function fail<T>(error: SocialError): SocialResult<T> {
  return { ok: false, error };
}

export function socialUrl(path: string, params?: Record<string, string>): string {
  const base = AUTH_URL + path;
  if (params === undefined) return base;
  const query = new URLSearchParams(params).toString();
  return query === '' ? base : base + '?' + query;
}

/**
 * The 401 body is plain text, and a proxy may substitute HTML for any status,
 * so an unreadable error body degrades to the fallback rather than failing.
 */
async function errorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body: unknown = await response.json();
    if (isRecord(body) && typeof body['error'] === 'string') {
      return body['error'];
    }
  } catch {
    // fall through
  }
  return fallback;
}

function retryAfterSeconds(response: Response): number | undefined {
  const raw = response.headers.get('Retry-After');
  if (raw === null) return undefined;
  const seconds = Number.parseInt(raw, DECIMAL_RADIX);
  return Number.isFinite(seconds) ? seconds : undefined;
}

async function toError(response: Response): Promise<SocialError> {
  const kind = KIND_BY_STATUS.get(response.status) ?? 'unknown';
  const message = await errorMessage(response, 'request failed (' + response.status + ')');
  const error: SocialError = { kind, status: response.status, message };
  if (kind !== 'rate_limited') return error;

  const seconds = retryAfterSeconds(response);
  return seconds === undefined ? error : { ...error, retryAfterSeconds: seconds };
}

export async function request<T>(
  url: string,
  init: RequestInit,
  decode: Decoder<T>
): Promise<SocialResult<T>> {
  let response: Response;
  try {
    response = await fetch(url, { ...init, credentials: 'include' });
  } catch {
    return fail({ kind: 'network', status: NETWORK_STATUS, message: 'network error' });
  }

  if (response.status < STATUS_OK_MIN || response.status > STATUS_OK_MAX) {
    return fail(await toError(response));
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return fail({ kind: 'unknown', status: response.status, message: 'malformed response' });
  }

  const value = decode(body);
  if (value === null) {
    return fail({ kind: 'unknown', status: response.status, message: 'unexpected response' });
  }
  return ok(value);
}

export function jsonRequest<T>(
  url: string,
  method: string,
  body: unknown,
  decode: Decoder<T>
): Promise<SocialResult<T>> {
  return request(
    url,
    {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
    decode
  );
}
